<template>
  <div class="page">
    <header class="head">
      <div class="title-wrap">
        <div class="eyebrow">DEVICE USAGE</div>
        <h2 class="h2">기기별 사용시간</h2>
        <p class="sub">
          학교별 기기 사용 현황과 상세 기록을 확인할 수 있습니다.
        </p>
      </div>

      <div class="device-count" aria-label="조회된 기기 수">
        <span>조회 기기</span>
        <strong>{{ filteredDevices.length }}</strong>
        <span>대</span>
      </div>
    </header>

    <section class="panel">
      <div class="toolbar">
        <div class="tool-left">
          <input
            v-model.trim="q"
            class="input"
            placeholder="학교명, 학교지역, 위치 검색"
            aria-label="기기 검색"
          />
        </div>

        <div class="tool-right">
          <button
            class="btn btn-dark"
            type="button"
            :disabled="!filteredDevices.length"
            @click="openPdfPreview"
          >
            PDF 미리보기
          </button>
          <button class="btn btn-ghost" type="button" @click="resetFilters">
            필터 초기화
          </button>
        </div>
      </div>

      <section class="usage-stats" aria-label="필터 결과 사용시간 통계">
        <article class="stat-card primary">
          <span class="stat-label">총 사용시간</span>
          <strong>{{ usageSummary.totalText }}</strong>
          <span class="stat-note">필터된 {{ usageSummary.count }}대 기준</span>
        </article>
        <article class="stat-card">
          <span class="stat-label">평균 사용시간</span>
          <strong>{{ usageSummary.averageText }}</strong>
          <span class="stat-note">기기 1대당 평균</span>
        </article>
        <article class="stat-card">
          <span class="stat-label">중앙값</span>
          <strong>{{ usageSummary.medianText }}</strong>
          <span class="stat-note">극단값 영향을 줄인 기준</span>
        </article>
      </section>

      <section class="distribution-panel" aria-label="필터 결과 사용시간 분포">
        <div class="distribution-head">
          <div>
            <strong>사용시간 분포</strong>
            <span
              >필터된 기기들이 어느 사용시간대에 몰려 있는지 보여줍니다.</span
            >
          </div>
          <span class="distribution-range">
            일반 범위 최대 {{ usageDistributionStats.upperFenceText }} / 벗어난
            기기 {{ usageDistributionStats.outlierCount }}대
          </span>
        </div>
        <div v-if="filteredDevices.length" class="distribution-chart">
          <Bar
            :data="usageDistributionChartData"
            :options="usageDistributionChartOptions"
          />
        </div>
        <div v-if="usageOutlierRows.length" class="outlier-list">
          <div class="outlier-list-head">
            <strong>범위 밖 데이터</strong>
            <span>
              상위 {{ usageOutlierRows.length }}대 표시
              <template v-if="usageDistributionStats.hiddenOutlierCount">
                / 외 {{ usageDistributionStats.hiddenOutlierCount }}대
              </template>
            </span>
          </div>
          <ul class="outlier-items" aria-label="범위 밖 사용시간 목록">
            <li v-for="row in usageOutlierRows" :key="row.key">
              <div>
                <strong>{{ row.schoolName }}</strong>
                <span>{{ row.meta }}</span>
              </div>
              <b>{{ row.usageText }}</b>
            </li>
          </ul>
        </div>
        <div v-if="!filteredDevices.length" class="distribution-empty">
          분포를 표시할 기기 데이터가 없습니다.
        </div>
      </section>

      <div class="table-wrap">
        <table class="table">
          <thead>
            <tr>
              <th class="col-name">학교명</th>
              <th class="col-region">학교지역</th>
              <th class="col-status">
                <div class="status-head">
                  <span>상태</span>
                  <select
                    v-model="statusFilter"
                    class="status-filter"
                    aria-label="상태 필터"
                  >
                    <option value="ALL">전체</option>
                    <option value="ACTIVE">활성</option>
                    <option value="INACTIVE">비활성</option>
                  </select>
                </div>
              </th>
              <th class="col-location">위치</th>
              <th class="col-usage">총사용시간</th>
              <th class="col-actions right">작업</th>
            </tr>
          </thead>

          <tbody v-if="filteredDevices.length > 0">
            <tr v-for="d in filteredDevices" :key="d.device_uuid">
              <td>
                <div class="strong">{{ d.school_name }}</div>
              </td>
              <td>{{ d.school_region || "-" }}</td>
              <td>
                <span
                  class="status-badge"
                  :class="{ inactive: !isDeviceActive(d) }"
                >
                  {{ getDeviceStatusLabel(d) }}
                </span>
              </td>
              <td>
                <span class="location">{{ d.app_location || "-" }}</span>
              </td>
              <td>
                <div class="usage-time">
                  <span class="usage-icon" aria-hidden="true">⏱</span>
                  <strong>{{ formatTotalUsage(d.total_usage_sec) }}</strong>
                </div>
              </td>
              <td>
                <div class="actions">
                  <button
                    class="link link-edit"
                    type="button"
                    @click="openEditDeviceModal(d)"
                  >
                    수정
                  </button>
                  <button
                    class="link"
                    type="button"
                    @click="openUseTimeModal(d)"
                  >
                    사용시간 조회
                  </button>
                  <button
                    class="link"
                    type="button"
                    @click="openUseMonthModal(d)"
                  >
                    월별 사용시간 조회
                  </button>
                </div>
              </td>
            </tr>
          </tbody>

          <tbody v-else>
            <tr>
              <td class="empty" colspan="6">
                결과가 없습니다. 검색어를 확인하세요.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- 디바이스 수정 모달 -->
    <div
      v-if="showEditDeviceModal"
      class="modal-overlay"
      @click.self="closeEditDeviceModal"
    >
      <div class="modal edit-device-modal">
        <header class="modal-header">
          <div class="modal-heading">
            <div class="modal-icon edit" aria-hidden="true">✎</div>
            <div>
              <div class="modal-title">디바이스 정보 수정</div>
              <p class="modal-subtitle">
                {{ editDeviceTarget?.school_name || "-" }}의 등록 정보를
                수정합니다.
              </p>
            </div>
          </div>
          <button
            class="modal-close"
            type="button"
            aria-label="디바이스 수정 모달 닫기"
            :disabled="isUpdatingDevice"
            @click="closeEditDeviceModal"
          >
            ×
          </button>
        </header>

        <form class="edit-device-form" @submit.prevent="updateDevice">
          <label class="edit-field">
            <span>디바이스 UUID</span>
            <input
              v-model="editDeviceForm.deviceUuid"
              class="input"
              type="text"
              readonly
            />
          </label>

          <label class="edit-field">
            <span>학교 ID</span>
            <input
              v-model.number="editDeviceForm.schoolId"
              class="input"
              type="number"
              readonly
            />
          </label>

          <label class="edit-field">
            <span>디바이스 위치</span>
            <input
              v-model.trim="editDeviceForm.appLocation"
              class="input"
              type="text"
              placeholder="예: 3층 컴퓨터실"
              :disabled="isUpdatingDevice"
            />
          </label>

          <fieldset class="edit-time-field">
            <legend>이전 사용시간</legend>
            <div class="edit-time-inputs">
              <label>
                <input
                  v-model.number="editDeviceForm.baseUsageHours"
                  class="input"
                  type="number"
                  min="0"
                  step="any"
                  placeholder="예: 1002.5"
                  required
                  :disabled="isUpdatingDevice"
                />
                <span>시간</span>
              </label>
            </div>
            <small>
              저장 시 {{ editBaseUsageSeconds.toLocaleString() }}초로
              변환됩니다.
            </small>
          </fieldset>

          <div class="modal-actions edit-modal-actions">
            <button
              class="btn btn-ghost"
              type="button"
              :disabled="isUpdatingDevice"
              @click="closeEditDeviceModal"
            >
              취소
            </button>
            <button
              class="btn btn-primary"
              type="submit"
              :disabled="isUpdatingDevice"
            >
              {{ isUpdatingDevice ? "수정 중..." : "수정" }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- 사용시간 조회 모달 -->
    <div
      v-if="showUseTimeModal"
      class="modal-overlay"
      @click.self="closeUseTimeModal"
    >
      <div class="modal wide">
        <header class="modal-header">
          <div class="modal-heading">
            <div class="modal-icon" aria-hidden="true">⏱</div>
            <div>
              <div class="modal-title">사용시간 조회</div>
              <p class="modal-subtitle">
                {{ useTimeTarget?.school_name }}의 일별 사용 현황
              </p>
            </div>
          </div>
          <button
            class="modal-close"
            type="button"
            aria-label="모달 닫기"
            @click="closeUseTimeModal"
          >
            ×
          </button>
        </header>

        <div class="summary-grid">
          <div class="summary-card">
            <span class="summary-label">이전 사용시간</span>
            <strong>{{ secondsToTime(useTimeTarget?.base_usage_sec) }}</strong>
            <span class="summary-note">등록 전 누적 기록</span>
          </div>
          <div class="summary-card primary">
            <span class="summary-label">총 사용시간</span>
            <strong>{{ secondsToTime(useTimeTarget?.total_usage_sec) }}</strong>
            <span class="summary-note">현재까지 전체 누적</span>
          </div>
        </div>

        <section class="query-panel">
          <div class="query-heading">
            <strong>조회 기간</strong>
            <span>확인할 날짜 범위를 선택하세요.</span>
          </div>
          <div class="date-row">
            <label class="field">
              <div class="label">시작일</div>
              <input
                v-model="useTimeForm.startDate"
                class="input"
                type="date"
              />
            </label>

            <label class="field">
              <div class="label">종료일</div>
              <input v-model="useTimeForm.endDate" class="input" type="date" />
            </label>

            <div class="field button-field">
              <button
                class="btn btn-primary query-button"
                type="button"
                @click="getDeviceUseTime"
              >
                조회하기
              </button>
            </div>
          </div>
        </section>

        <section class="chart-section">
          <div class="chart-header">
            <div>
              <strong>일별 사용 추이</strong>
              <span>선택한 기간의 사용시간을 일 단위로 보여줍니다.</span>
            </div>
            <div class="period-total">
              기간 합계 <strong>{{ secondsToTime(useTimePeriodTotal) }}</strong>
            </div>
          </div>

          <div v-if="useTimeLoading" class="chart-empty loading-state">
            <span class="spinner" aria-hidden="true"></span>
            사용시간 데이터를 불러오고 있습니다.
          </div>

          <div v-else-if="useTimeList.length > 0" class="chart-wrap">
            <Bar :data="usageChartData" :options="usageChartOptions" />
          </div>

          <div v-else class="chart-empty">
            조회된 사용시간 데이터가 없습니다.
          </div>
        </section>

        <div class="modal-actions">
          <button
            class="btn btn-ghost"
            type="button"
            @click="closeUseTimeModal"
          >
            닫기
          </button>
        </div>
      </div>
    </div>

    <!-- 월별 사용시간 조회 모달 -->
    <div
      v-if="showUseMonthModal"
      class="modal-overlay"
      @click.self="closeUseMonthModal"
    >
      <div class="modal wide">
        <header class="modal-header">
          <div class="modal-heading">
            <div class="modal-icon month" aria-hidden="true">▥</div>
            <div>
              <div class="modal-title">월별 사용시간 조회</div>
              <p class="modal-subtitle">
                {{ useMonthTarget?.school_name }}의 월별 사용 현황
              </p>
            </div>
          </div>
          <button
            class="modal-close"
            type="button"
            aria-label="모달 닫기"
            @click="closeUseMonthModal"
          >
            ×
          </button>
        </header>

        <div class="summary-grid">
          <div class="summary-card">
            <span class="summary-label">이전 사용시간</span>
            <strong>{{ secondsToTime(useMonthTarget?.base_usage_sec) }}</strong>
            <span class="summary-note">등록 전 누적 기록</span>
          </div>
          <div class="summary-card primary month">
            <span class="summary-label">총 사용시간</span>
            <strong>{{
              secondsToTime(useMonthTarget?.total_usage_sec)
            }}</strong>
            <span class="summary-note">현재까지 전체 누적</span>
          </div>
        </div>

        <section class="query-panel">
          <div class="query-heading">
            <strong>조회 기간</strong>
            <span>확인할 월 범위를 선택하세요.</span>
          </div>
          <div class="date-row">
            <label class="field">
              <div class="label">시작월</div>
              <input
                v-model="useMonthForm.startMonth"
                class="input"
                type="month"
              />
            </label>

            <label class="field">
              <div class="label">종료월</div>
              <input
                v-model="useMonthForm.endMonth"
                class="input"
                type="month"
              />
            </label>

            <div class="field button-field">
              <button
                class="btn btn-primary query-button"
                type="button"
                @click="getDeviceUseMonth"
              >
                조회하기
              </button>
            </div>
          </div>
        </section>

        <section class="chart-section">
          <div class="chart-header">
            <div>
              <strong>월별 사용 추이</strong>
              <span>선택한 기간의 사용시간을 월 단위로 보여줍니다.</span>
            </div>
            <div class="period-total month">
              기간 합계
              <strong>{{ secondsToTime(useMonthPeriodTotal) }}</strong>
            </div>
          </div>

          <div v-if="useMonthLoading" class="chart-empty loading-state">
            <span class="spinner month" aria-hidden="true"></span>
            월별 사용시간 데이터를 불러오고 있습니다.
          </div>

          <div v-else-if="useMonthList.length > 0" class="chart-wrap">
            <Bar
              :data="usageMonthChartData"
              :options="usageMonthChartOptions"
            />
          </div>

          <div v-else class="chart-empty">
            조회된 월별 사용시간 데이터가 없습니다.
          </div>
        </section>

        <div class="modal-actions">
          <button
            class="btn btn-ghost"
            type="button"
            @click="closeUseMonthModal"
          >
            닫기
          </button>
        </div>
      </div>
    </div>

    <!-- 기기별 사용시간 PDF 미리보기 모달 -->
    <div
      v-if="pdfModalVisible"
      class="modal-overlay pdf-modal-overlay"
      @click.self="closePdfPreview"
    >
      <div class="pdf-modal-card">
        <header class="pdf-modal-header">
          <div>
            <div class="modal-title">기기별 사용시간 PDF 미리보기</div>
            <p class="pdf-modal-subtitle">
              목록과 같은 검색 조건과 월 범위를 적용해 PDF를 생성합니다.
            </p>
          </div>
          <button
            class="modal-close"
            type="button"
            aria-label="PDF 미리보기 닫기"
            @click="closePdfPreview"
          >
            ×
          </button>
        </header>

        <section class="pdf-range-panel">
          <label class="field pdf-filter-field">
            <span class="label">목록 필터</span>
            <input
              v-model.trim="pdfQuery"
              class="input"
              type="search"
              placeholder="학교명, 학교지역, 위치 검색"
              :disabled="isGeneratingPdf"
              @input="clearPdfResult"
            />
          </label>
          <label class="field">
            <span class="label">시작월</span>
            <input
              v-model="pdfForm.startMonth"
              class="input"
              type="month"
              :disabled="isGeneratingPdf"
              @change="clearPdfResult"
            />
          </label>
          <span class="range-separator" aria-hidden="true">~</span>
          <label class="field">
            <span class="label">종료월</span>
            <input
              v-model="pdfForm.endMonth"
              class="input"
              type="month"
              :disabled="isGeneratingPdf"
              @change="clearPdfResult"
            />
          </label>
          <button
            class="btn btn-dark"
            type="button"
            :disabled="isGeneratingPdf || !pdfFilteredDevices.length"
            @click="generatePdfPreview"
          >
            {{ isGeneratingPdf ? "PDF 생성 중..." : "미리보기 생성" }}
          </button>
          <span class="pdf-result-summary">
            PDF 대상 {{ pdfFilteredDevices.length }}대
            <template v-if="pdfPreviewUrl">
              · {{ pdfPeriodLabel }} · {{ pdfPageChunks.length }}페이지
            </template>
          </span>
        </section>

        <div class="pdf-modal-body">
          <iframe
            v-if="pdfPreviewUrl"
            :src="pdfPreviewUrl"
            class="pdf-frame"
            title="기기별 사용시간 PDF 미리보기"
          ></iframe>
          <div v-else class="pdf-preview-empty">
            <span v-if="isGeneratingPdf" class="spinner"></span>
            <strong>{{
              isGeneratingPdf
                ? "기기별 사용시간을 불러와 PDF를 만들고 있습니다."
                : "월 범위를 선택한 뒤 미리보기 생성을 눌러주세요."
            }}</strong>
          </div>

          <div class="pdf-capture-area" aria-hidden="true">
            <div ref="pdfCapturePagesRef" class="pdf-capture-stack">
              <article
                v-for="(pageRows, pageIndex) in pdfPageChunks"
                :key="`usage-pdf-${pageIndex}`"
                class="pdf-page"
              >
                <header class="pdf-page-header">
                  <div>
                    <h1>기기별 사용시간</h1>
                    <p>조회 기간: {{ pdfPeriodLabel }}</p>
                  </div>
                  <strong
                    >{{ pageIndex + 1 }} / {{ pdfPageChunks.length }}</strong
                  >
                </header>

                <table class="pdf-table">
                  <thead>
                    <tr>
                      <th class="pdf-col-number">번호</th>
                      <th>학교명</th>
                      <th>학교지역</th>
                      <th>상태</th>
                      <th>위치</th>
                      <th class="pdf-col-usage">총 사용시간</th>
                      <th class="pdf-col-period-usage">
                        {{ pdfPeriodUsageColumnLabel }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in pageRows" :key="row.pdfRowId">
                      <td>{{ row.number }}</td>
                      <td>{{ row.schoolName }}</td>
                      <td>{{ row.schoolRegion }}</td>
                      <td>{{ row.statusText }}</td>
                      <td>{{ row.location }}</td>
                      <td>{{ row.totalUsageText }}</td>
                      <td class="pdf-usage-cell">{{ row.periodUsageText }}</td>
                    </tr>
                  </tbody>
                </table>

                <footer class="pdf-page-footer">
                  <span>총 {{ pdfRows.length }}대</span>
                  <span>출력일시 {{ pdfPrintedAt }}</span>
                </footer>
              </article>
            </div>
          </div>
        </div>

        <footer class="pdf-modal-footer">
          <button class="btn btn-ghost" type="button" @click="closePdfPreview">
            닫기
          </button>
          <button
            class="btn btn-primary"
            type="button"
            :disabled="!pdfPreviewUrl || isGeneratingPdf || isDownloadingPdf"
            @click="downloadPdf"
          >
            {{ isDownloadingPdf ? "다운로드 중..." : "PDF 다운로드" }}
          </button>
        </footer>
      </div>
    </div>

    <!-- Toast -->
    <div v-if="toast.show" class="toast">{{ toast.message }}</div>
  </div>
</template>

<script setup>
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref
} from "vue";
import { deviceApi } from "@/api";

import { Bar } from "vue-chartjs";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  LineController,
  BarElement,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale
} from "chart.js";

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  LineController,
  BarElement,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale
);

onMounted(() => {
  getDeviceList();
});

/**
 * 디바이스 조회
 */
const devices = ref([]);

async function getDeviceList() {
  try {
    const result = await deviceApi.getDevice();
    console.log("디바이스 조회 결과:", result);

    devices.value = Array.isArray(result) ? result : [];
  } catch (error) {
    console.error("디바이스 조회 실패:", error);
    devices.value = [];
    showToast("디바이스 조회 실패");
  }
}

/**
 * 디바이스 정보 수정
 */
const showEditDeviceModal = ref(false);
const editDeviceTarget = ref(null);
const isUpdatingDevice = ref(false);
const editDeviceForm = reactive({
  deviceUuid: "",
  schoolId: "",
  appLocation: "",
  baseUsageHours: 0
});

const editBaseUsageSeconds = computed(() => {
  const hours = Number(editDeviceForm.baseUsageHours);

  if (!Number.isFinite(hours) || hours < 0) return 0;
  return Math.round(hours * 3600);
});

function openEditDeviceModal(device) {
  const baseUsageSeconds = Math.max(
    0,
    Math.trunc(Number(device.base_usage_sec) || 0)
  );

  editDeviceTarget.value = device;
  editDeviceForm.deviceUuid = device.device_uuid || "";
  editDeviceForm.schoolId = device.school_id ?? device.school_idx ?? "";
  editDeviceForm.appLocation = device.app_location || "";
  editDeviceForm.baseUsageHours = Number((baseUsageSeconds / 3600).toFixed(6));
  showEditDeviceModal.value = true;
}

function closeEditDeviceModal() {
  if (isUpdatingDevice.value) return;

  showEditDeviceModal.value = false;
  editDeviceTarget.value = null;
}

async function updateDevice() {
  if (isUpdatingDevice.value) return;

  const schoolId = Number(editDeviceForm.schoolId);
  const hours = Number(editDeviceForm.baseUsageHours);

  if (!editDeviceForm.deviceUuid) {
    showToast("디바이스 UUID를 확인해주세요.");
    return;
  }

  if (!Number.isInteger(schoolId) || schoolId < 1) {
    showToast("학교 ID는 1 이상의 정수로 입력해주세요.");
    return;
  }

  if (!Number.isFinite(hours) || hours < 0) {
    showToast("이전 사용시간을 0 이상의 시간 값으로 입력해주세요.");
    return;
  }

  try {
    isUpdatingDevice.value = true;
    const updated = await deviceApi.updateDevice({
      device_uuid: editDeviceForm.deviceUuid,
      school_id: schoolId,
      app_location: editDeviceForm.appLocation,
      base_usage_sec: editBaseUsageSeconds.value
    });

    if (!updated) {
      showToast("디바이스 정보를 수정하지 못했습니다.");
      return;
    }

    await getDeviceList();
    isUpdatingDevice.value = false;
    closeEditDeviceModal();
    showToast("디바이스 정보가 수정되었습니다.");
  } catch (error) {
    console.error("디바이스 정보 수정 실패:", error);
    showToast("디바이스 정보 수정 중 오류가 발생했습니다.");
  } finally {
    isUpdatingDevice.value = false;
  }
}

/**
 * 목록 필터
 */
const q = ref("");
const statusFilter = ref("ALL");

function isDeviceActive(device) {
  return String(device?.status || "").toUpperCase() === "ACTIVE";
}

function getDeviceStatusLabel(device) {
  return isDeviceActive(device) ? "활성" : "비활성";
}

function filterAndSortDevices(source, query, status = "ALL") {
  let list = Array.isArray(source) ? source : [];
  const keyword = String(query || "")
    .trim()
    .toLowerCase();
  const statusValue = String(status || "ALL").toUpperCase();

  if (keyword) {
    list = list.filter((d) => {
      return (
        String(d.school_region || "")
          .toLowerCase()
          .includes(keyword) ||
        String(d.school_name || "")
          .toLowerCase()
          .includes(keyword) ||
        String(d.app_location || "")
          .toLowerCase()
          .includes(keyword)
      );
    });
  }

  if (statusValue === "ACTIVE") {
    list = list.filter((d) => isDeviceActive(d));
  } else if (statusValue === "INACTIVE") {
    list = list.filter((d) => !isDeviceActive(d));
  }

  return [...list].sort((a, b) =>
    String(a.created_at || "") < String(b.created_at || "") ? 1 : -1
  );
}

const filteredDevices = computed(() =>
  filterAndSortDevices(devices.value, q.value, statusFilter.value)
);

function getUsageSeconds(device) {
  return Math.max(0, Number(device?.total_usage_sec) || 0);
}

function getMedian(values) {
  if (!values.length) return 0;

  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);

  if (sorted.length % 2) return sorted[middle];
  return Math.round((sorted[middle - 1] + sorted[middle]) / 2);
}

function getQuantile(sortedValues, quantile) {
  if (!sortedValues.length) return 0;

  const position = (sortedValues.length - 1) * quantile;
  const base = Math.floor(position);
  const rest = position - base;
  const next = sortedValues[base + 1];

  if (next === undefined) return sortedValues[base];
  return Math.round(sortedValues[base] + rest * (next - sortedValues[base]));
}

const usageSummary = computed(() => {
  const list = filteredDevices.value;
  const values = list.map(getUsageSeconds);
  const total = values.reduce((sum, value) => sum + value, 0);
  const count = values.length;
  const average = count ? Math.round(total / count) : 0;
  const median = getMedian(values);
  const max = values.length ? Math.max(...values) : 0;
  const min = values.length ? Math.min(...values) : 0;

  return {
    total,
    count,
    average,
    median,
    max,
    min,
    totalText: formatTotalUsage(total),
    averageText: formatTotalUsage(average),
    medianText: formatTotalUsage(median),
    maxText: formatTotalUsage(max),
    minText: formatTotalUsage(min)
  };
});

function formatDistributionLabel(sec) {
  const totalSec = Math.max(0, Math.round(Number(sec) || 0));
  const hours = Math.floor(totalSec / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);

  if (hours > 0 && minutes > 0) return `${hours}시간 ${minutes}분`;
  if (hours > 0) return `${hours}시간`;
  return `${minutes}분`;
}

function getDistributionStats(values) {
  const sortedValues = [...values].sort((a, b) => a - b);

  if (sortedValues.length < 4) {
    return {
      values: sortedValues,
      inlierValues: sortedValues,
      outlierValues: [],
      upperFence: sortedValues[sortedValues.length - 1] || 0
    };
  }

  const q1 = getQuantile(sortedValues, 0.25);
  const q3 = getQuantile(sortedValues, 0.75);
  const iqr = q3 - q1;
  const upperFence = Math.max(q3, Math.round(q3 + iqr * 1.5));
  const inlierValues = sortedValues.filter((value) => value <= upperFence);
  const outlierValues = sortedValues.filter((value) => value > upperFence);

  return {
    values: sortedValues,
    inlierValues: inlierValues.length ? inlierValues : sortedValues,
    outlierValues,
    q1,
    q3,
    iqr,
    upperFence
  };
}

function getDistributionBuckets(values) {
  if (!values.length) return [];

  const max = Math.max(...values);

  if (max <= 0) {
    return [
      {
        label: "0분",
        center: 0,
        count: values.length
      }
    ];
  }

  const targetBucketCount = Math.min(
    7,
    Math.max(4, Math.ceil(Math.sqrt(values.length)))
  );
  const rawStep = max / targetBucketCount;
  const step = Math.max(1800, Math.ceil(rawStep / 1800) * 1800);
  const bucketCount = Math.max(1, Math.ceil(max / step));

  const buckets = Array.from({ length: bucketCount }, (_, index) => {
    const min = index * step;
    const maxValue = (index + 1) * step;

    return {
      min,
      max: maxValue,
      center: min + step / 2,
      count: 0,
      label: `${formatDistributionLabel(min)}~${formatDistributionLabel(
        maxValue
      )}`
    };
  });

  values.forEach((value) => {
    const bucketIndex = Math.min(Math.floor(value / step), buckets.length - 1);
    buckets[bucketIndex].count += 1;
  });

  return buckets;
}

const usageDistributionModel = computed(() =>
  getDistributionStats(filteredDevices.value.map(getUsageSeconds))
);

const usageDistributionBuckets = computed(() =>
  getDistributionBuckets(usageDistributionModel.value.inlierValues)
);

const usageDistributionStats = computed(() => {
  const model = usageDistributionModel.value;
  const outlierLimit = 5;

  return {
    upperFenceText: formatTotalUsage(model.upperFence || 0),
    outlierCount: model.outlierValues.length,
    hiddenOutlierCount: Math.max(0, model.outlierValues.length - outlierLimit)
  };
});

const usageOutlierRows = computed(() => {
  const model = usageDistributionModel.value;
  const upperFence = model.upperFence || 0;

  return filteredDevices.value
    .map((device, index) => ({
      key: `${device.device_uuid || "device"}-${index}`,
      schoolName: device.school_name || "-",
      meta: [device.school_region, device.app_location]
        .filter(Boolean)
        .join(" / "),
      usageSec: getUsageSeconds(device)
    }))
    .filter((row) => row.usageSec > upperFence)
    .sort((a, b) => b.usageSec - a.usageSec)
    .slice(0, 5)
    .map((row) => ({
      ...row,
      meta: row.meta || "-",
      usageText: formatTotalUsage(row.usageSec)
    }));
});

const usageDistributionChartData = computed(() => {
  const buckets = usageDistributionBuckets.value;
  const values = usageDistributionModel.value.inlierValues;
  const average = values.length
    ? Math.round(values.reduce((sum, value) => sum + value, 0) / values.length)
    : 0;
  const maxCount = Math.max(...buckets.map((bucket) => bucket.count), 1);
  const variance = values.length
    ? values.reduce((sum, value) => sum + (value - average) ** 2, 0) /
      values.length
    : 0;
  const standardDeviation = Math.sqrt(variance);
  const normalRawValues = buckets.map((bucket) => {
    if (!standardDeviation) return bucket.count;

    const exponent =
      -((bucket.center - average) ** 2) / (2 * standardDeviation ** 2);
    return Math.exp(exponent);
  });
  const maxNormalValue = Math.max(...normalRawValues, 1);

  return {
    labels: buckets.map((bucket) => bucket.label),
    datasets: [
      {
        type: "bar",
        label: "기기 수",
        data: buckets.map((bucket) => bucket.count),
        backgroundColor: (context) =>
          createBarGradient(context, "#99f6e4", "#14b8a6"),
        hoverBackgroundColor: "#0f766e",
        borderWidth: 0,
        borderRadius: 7,
        borderSkipped: false,
        maxBarThickness: 52,
        categoryPercentage: 0.72,
        barPercentage: 0.86
      },
      {
        type: "line",
        label: "평균 기반 곡선",
        data: normalRawValues.map((value) =>
          Number(((value / maxNormalValue) * maxCount).toFixed(2))
        ),
        borderColor: "#1d4ed8",
        backgroundColor: "rgba(29, 78, 216, 0.08)",
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 3,
        tension: 0.35
      }
    ]
  };
});

const usageDistributionChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  animation: {
    duration: 650,
    easing: "easeOutQuart"
  },
  interaction: {
    mode: "index",
    intersect: false
  },
  plugins: {
    legend: {
      display: true,
      position: "top",
      labels: {
        usePointStyle: true,
        boxWidth: 8,
        color: "#64748b",
        font: {
          size: 11,
          weight: "bold"
        }
      }
    },
    tooltip: {
      backgroundColor: "#0f172a",
      titleColor: "#cbd5e1",
      bodyColor: "#ffffff",
      padding: 12,
      cornerRadius: 10,
      callbacks: {
        label(context) {
          if (context.dataset.type === "line") {
            return `곡선 기준 ${context.raw}`;
          }

          return `기기 수 ${context.raw}대`;
        }
      }
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        precision: 0,
        color: "#94a3b8",
        font: {
          size: 11
        }
      },
      border: {
        display: false
      },
      grid: {
        color: "rgba(148, 163, 184, 0.18)",
        drawTicks: false
      }
    },
    x: {
      border: {
        display: false
      },
      grid: {
        display: false
      },
      ticks: {
        color: "#64748b",
        maxRotation: 0,
        autoSkip: true,
        font: {
          size: 11
        }
      }
    }
  }
};

function resetFilters() {
  q.value = "";
  statusFilter.value = "ALL";
}

/**
 * 기기별 사용시간 PDF
 */
const pdfModalVisible = ref(false);
const pdfCapturePagesRef = ref(null);
const pdfRows = ref([]);
const pdfPageChunks = ref([]);
const pdfPreviewUrl = ref("");
const currentPdfBlob = ref(null);
const isGeneratingPdf = ref(false);
const isDownloadingPdf = ref(false);
const pdfPrintedAt = ref("");
const pdfQuery = ref("");

const pdfForm = reactive({
  startMonth: "",
  endMonth: ""
});

const pdfFilteredDevices = computed(() =>
  filterAndSortDevices(devices.value, pdfQuery.value, statusFilter.value)
);

const pdfPeriodLabel = computed(() => {
  if (!pdfForm.startMonth || !pdfForm.endMonth) return "-";

  const formatLabel = (value) => {
    const [year, month] = value.split("-");
    return `${year}년 ${Number(month)}월`;
  };

  if (pdfForm.startMonth === pdfForm.endMonth) {
    return formatLabel(pdfForm.startMonth);
  }

  return `${formatLabel(pdfForm.startMonth)} ~ ${formatLabel(
    pdfForm.endMonth
  )}`;
});

const pdfPeriodUsageColumnLabel = computed(() => {
  if (!pdfForm.startMonth || !pdfForm.endMonth) return "선택 기간 사용시간";

  const [startYear, startMonth] = pdfForm.startMonth.split("-");
  const [endYear, endMonth] = pdfForm.endMonth.split("-");

  if (pdfForm.startMonth === pdfForm.endMonth) {
    return `${Number(startMonth)}월 사용시간`;
  }

  if (startYear === endYear) {
    return `${Number(startMonth)}월~${Number(endMonth)}월 사용시간`;
  }

  return `${startYear}년 ${Number(startMonth)}월~${endYear}년 ${Number(
    endMonth
  )}월 사용시간`;
});

function cleanupPdfPreviewUrl() {
  if (pdfPreviewUrl.value) {
    URL.revokeObjectURL(pdfPreviewUrl.value);
    pdfPreviewUrl.value = "";
  }
}

function clearPdfResult() {
  cleanupPdfPreviewUrl();
  currentPdfBlob.value = null;
  pdfRows.value = [];
  pdfPageChunks.value = [];
}

function openPdfPreview() {
  if (!filteredDevices.value.length) {
    showToast("PDF로 만들 기기가 없습니다.");
    return;
  }

  const currentMonth = formatMonth(new Date());
  pdfQuery.value = q.value;
  pdfForm.startMonth = currentMonth;
  pdfForm.endMonth = currentMonth;
  clearPdfResult();
  pdfModalVisible.value = true;
}

function closePdfPreview() {
  pdfModalVisible.value = false;
  clearPdfResult();
}

function getMonthlyList(result, uuid) {
  if (Array.isArray(result)) return result;
  if (Array.isArray(result?.[uuid])) return result[uuid];
  return [];
}

async function buildPdfRows(sourceDevices) {
  const rows = new Array(sourceDevices.length);
  let cursor = 0;
  const workerCount = Math.min(6, sourceDevices.length);

  const worker = async () => {
    while (cursor < sourceDevices.length) {
      const index = cursor;
      cursor += 1;

      const device = sourceDevices[index];
      const result = await deviceApi.getDeviceUseMonth(
        device.device_uuid,
        pdfForm.startMonth,
        pdfForm.endMonth
      );
      const monthlyList = getMonthlyList(result, device.device_uuid);
      const totalSeconds = monthlyList.reduce(
        (sum, item) =>
          sum + Number(item.total_time_sec ?? item.usage_time_sec ?? 0),
        0
      );

      rows[index] = {
        pdfRowId: `${device.device_uuid || "device"}-${index}`,
        number: index + 1,
        schoolName: device.school_name || "-",
        schoolRegion: device.school_region || "-",
        statusText: getDeviceStatusLabel(device),
        location: device.app_location || "-",
        totalUsageText: formatTotalUsage(device.total_usage_sec),
        periodUsageText: formatTotalUsage(totalSeconds)
      };
    }
  };

  await Promise.all(Array.from({ length: workerCount }, () => worker()));
  return rows;
}

function chunkPdfRows(rows) {
  const rowsPerPage = 18;
  const pages = [];

  for (let index = 0; index < rows.length; index += rowsPerPage) {
    pages.push(rows.slice(index, index + rowsPerPage));
  }

  return pages;
}

async function renderPdfBlob() {
  const container = pdfCapturePagesRef.value;

  if (!container) throw new Error("PDF 캡처 영역이 없습니다.");

  const pageElements = Array.from(container.querySelectorAll(".pdf-page"));
  if (!pageElements.length) throw new Error("PDF 페이지가 없습니다.");

  const pdf = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4"
  });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 5;
  const availableWidth = pageWidth - margin * 2;
  const availableHeight = pageHeight - margin * 2;

  for (let index = 0; index < pageElements.length; index += 1) {
    const canvas = await html2canvas(pageElements[index], {
      scale: 1.5,
      useCORS: true,
      backgroundColor: "#ffffff",
      logging: false
    });
    const imageData = canvas.toDataURL("image/jpeg", 0.94);
    const ratio = Math.min(
      availableWidth / canvas.width,
      availableHeight / canvas.height
    );
    const renderWidth = canvas.width * ratio;
    const renderHeight = canvas.height * ratio;

    if (index > 0) pdf.addPage();
    pdf.addImage(
      imageData,
      "JPEG",
      (pageWidth - renderWidth) / 2,
      (pageHeight - renderHeight) / 2,
      renderWidth,
      renderHeight
    );
  }

  return pdf.output("blob");
}

async function generatePdfPreview() {
  if (isGeneratingPdf.value) return;

  if (!pdfForm.startMonth || !pdfForm.endMonth) {
    showToast("시작월과 종료월을 선택해주세요.");
    return;
  }

  if (pdfForm.startMonth > pdfForm.endMonth) {
    showToast("시작월은 종료월보다 늦을 수 없습니다.");
    return;
  }

  const sourceDevices = pdfFilteredDevices.value.map((device) => ({
    ...device
  }));
  if (!sourceDevices.length) {
    showToast("PDF로 만들 기기가 없습니다.");
    return;
  }

  try {
    isGeneratingPdf.value = true;
    clearPdfResult();
    pdfRows.value = await buildPdfRows(sourceDevices);
    pdfPageChunks.value = chunkPdfRows(pdfRows.value);
    pdfPrintedAt.value = new Intl.DateTimeFormat("ko-KR", {
      dateStyle: "medium",
      timeStyle: "short"
    }).format(new Date());

    await nextTick();
    const blob = await renderPdfBlob();
    currentPdfBlob.value = blob;
    pdfPreviewUrl.value = URL.createObjectURL(blob);
  } catch (error) {
    console.error("기기별 사용시간 PDF 생성 실패:", error);
    clearPdfResult();
    showToast("PDF 미리보기를 생성하지 못했습니다.");
  } finally {
    isGeneratingPdf.value = false;
  }
}

function getPdfFileName() {
  return `기기별_사용시간_${pdfForm.startMonth}_${pdfForm.endMonth}.pdf`;
}

async function downloadPdf() {
  if (!currentPdfBlob.value || isDownloadingPdf.value) return;

  try {
    isDownloadingPdf.value = true;
    const url = URL.createObjectURL(currentPdfBlob.value);
    const link = document.createElement("a");
    link.href = url;
    link.download = getPdfFileName();
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error("기기별 사용시간 PDF 다운로드 실패:", error);
    showToast("PDF 다운로드 중 오류가 발생했습니다.");
  } finally {
    isDownloadingPdf.value = false;
  }
}

onBeforeUnmount(() => {
  cleanupPdfPreviewUrl();
});

/**
 * 사용시간 조회
 */
const showUseTimeModal = ref(false);
const useTimeTarget = ref(null);
const useTimeList = ref([]);
const useTimeLoading = ref(false);

const useTimePeriodTotal = computed(() =>
  useTimeList.value.reduce(
    (total, item) => total + Number(item.usage_time_sec || 0),
    0
  )
);

const useTimeForm = reactive({
  startDate: "",
  endDate: ""
});

function formatDate(date) {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");

  return `${yyyy}-${mm}-${dd}`;
}

function getDefaultDateRange() {
  const today = new Date();
  const before30Days = new Date();

  before30Days.setDate(today.getDate() - 30);

  return {
    startDate: formatDate(before30Days),
    endDate: formatDate(today)
  };
}

function openUseTimeModal(device) {
  useTimeTarget.value = device;
  useTimeList.value = [];

  const range = getDefaultDateRange();
  useTimeForm.startDate = range.startDate;
  useTimeForm.endDate = range.endDate;

  showUseTimeModal.value = true;

  getDeviceUseTime();
}

function closeUseTimeModal() {
  showUseTimeModal.value = false;
  useTimeTarget.value = null;
  useTimeList.value = [];
}

async function getDeviceUseTime() {
  if (!useTimeTarget.value) return;

  const uuid = useTimeTarget.value.device_uuid;
  const { startDate, endDate } = useTimeForm;

  try {
    useTimeLoading.value = true;

    const result = await deviceApi.getDeviceUse(uuid, startDate, endDate);
    console.log("사용시간 조회 결과:", result);

    useTimeList.value = Array.isArray(result) ? result : [];
  } catch (error) {
    console.error("사용시간 조회 실패:", error);
    useTimeList.value = [];
    showToast("사용시간 조회 실패");
  } finally {
    useTimeLoading.value = false;
  }
}

/**
 * 월별 사용시간 조회
 */
const showUseMonthModal = ref(false);
const useMonthTarget = ref(null);
const useMonthList = ref([]);
const useMonthLoading = ref(false);

const useMonthPeriodTotal = computed(() =>
  useMonthList.value.reduce(
    (total, item) => total + Number(item.total_time_sec || 0),
    0
  )
);

const useMonthForm = reactive({
  startMonth: "",
  endMonth: ""
});

function formatMonth(date) {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");

  return `${yyyy}-${mm}`;
}

function getDefaultMonthRange() {
  const today = new Date();
  const before12Months = new Date();

  before12Months.setMonth(today.getMonth() - 11);

  return {
    startMonth: formatMonth(before12Months),
    endMonth: formatMonth(today)
  };
}

function openUseMonthModal(device) {
  useMonthTarget.value = device;
  useMonthList.value = [];

  const range = getDefaultMonthRange();
  useMonthForm.startMonth = range.startMonth;
  useMonthForm.endMonth = range.endMonth;

  showUseMonthModal.value = true;

  getDeviceUseMonth();
}

function closeUseMonthModal() {
  showUseMonthModal.value = false;
  useMonthTarget.value = null;
  useMonthList.value = [];
}

async function getDeviceUseMonth() {
  if (!useMonthTarget.value) return;

  const uuid = useMonthTarget.value.device_uuid;
  const { startMonth, endMonth } = useMonthForm;

  if (!startMonth || !endMonth) {
    showToast("시작월과 종료월을 선택해주세요.");
    return;
  }

  if (startMonth > endMonth) {
    showToast("시작월은 종료월보다 늦을 수 없습니다.");
    return;
  }

  try {
    useMonthLoading.value = true;

    const result = await deviceApi.getDeviceUseMonth(
      uuid,
      startMonth,
      endMonth
    );

    console.log("월별 사용시간 조회 결과:", result);

    const monthList = result?.[uuid];

    useMonthList.value = Array.isArray(monthList) ? monthList : [];
  } catch (error) {
    console.error("월별 사용시간 조회 실패:", error);
    useMonthList.value = [];
    showToast("월별 사용시간 조회 실패");
  } finally {
    useMonthLoading.value = false;
  }
}

/**
 * 사용시간 차트
 */
function secondsToTime(sec) {
  const totalSec = Number(sec || 0);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);

  if (h > 0) return `${h}시간 ${m}분`;
  return `${m}분`;
}

function formatTotalUsage(sec) {
  const totalSec = Math.max(0, Number(sec) || 0);
  const hours = Math.floor(totalSec / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);

  return `${hours}시간 ${minutes}분`;
}

function getDateRangeList(startDate, endDate) {
  const list = [];

  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);

  const current = new Date(start);

  while (current <= end) {
    list.push(formatDate(current));
    current.setDate(current.getDate() + 1);
  }

  return list;
}

function groupUsageByDate(list, startDate, endDate) {
  const map = {};

  list.forEach((item) => {
    const date = item.usage_date;
    const seconds = Number(item.usage_time_sec || 0);

    if (!date) return;

    map[date] = (map[date] || 0) + seconds;
  });

  return getDateRangeList(startDate, endDate).map((date) => {
    const seconds = map[date] || 0;

    return {
      date,
      seconds,
      hours: Number((seconds / 3600).toFixed(2))
    };
  });
}

function createBarGradient(context, startColor, endColor) {
  const { chart } = context;
  const { ctx, chartArea } = chart;

  if (!chartArea) return startColor;

  const gradient = ctx.createLinearGradient(
    0,
    chartArea.bottom,
    0,
    chartArea.top
  );
  gradient.addColorStop(0, startColor);
  gradient.addColorStop(1, endColor);

  return gradient;
}

function createUsageChartOptions(axisLabel) {
  return {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 650,
      easing: "easeOutQuart"
    },
    interaction: {
      mode: "index",
      intersect: false
    },
    layout: {
      padding: {
        top: 8
      }
    },
    parsing: {
      xAxisKey: "x",
      yAxisKey: "y"
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        displayColors: false,
        backgroundColor: "#0f172a",
        titleColor: "#cbd5e1",
        bodyColor: "#ffffff",
        padding: 12,
        cornerRadius: 10,
        titleFont: {
          size: 12,
          weight: "normal"
        },
        bodyFont: {
          size: 13,
          weight: "bold"
        },
        callbacks: {
          label(context) {
            const seconds = context.raw?.seconds || 0;
            return `사용시간  ${secondsToTime(seconds)}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        border: {
          display: false
        },
        grid: {
          color: "rgba(148, 163, 184, 0.18)",
          drawTicks: false
        },
        ticks: {
          color: "#94a3b8",
          padding: 10,
          font: {
            size: 11
          },
          callback(value) {
            return `${value}시간`;
          }
        }
      },
      x: {
        border: {
          display: false
        },
        grid: {
          display: false
        },
        title: {
          display: true,
          text: axisLabel,
          color: "#94a3b8",
          padding: {
            top: 12
          },
          font: {
            size: 11,
            weight: "normal"
          }
        },
        ticks: {
          color: "#64748b",
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 12,
          font: {
            size: 11
          }
        }
      }
    }
  };
}

const usageChartData = computed(() => {
  const grouped = groupUsageByDate(
    useTimeList.value,
    useTimeForm.startDate,
    useTimeForm.endDate
  );

  return {
    labels: grouped.map((item) => item.date),
    datasets: [
      {
        label: "사용시간",
        data: grouped.map((item) => ({
          x: item.date,
          y: item.hours,
          seconds: item.seconds
        })),
        backgroundColor: (context) =>
          createBarGradient(context, "#60a5fa", "#2563eb"),
        hoverBackgroundColor: "#1d4ed8",
        borderWidth: 0,
        borderRadius: 7,
        borderSkipped: false,
        maxBarThickness: 34,
        categoryPercentage: 0.72,
        barPercentage: 0.82
      }
    ]
  };
});

const usageChartOptions = createUsageChartOptions("날짜");

const usageMonthChartData = computed(() => {
  return {
    labels: useMonthList.value.map((item) => item.usage_month),
    datasets: [
      {
        label: "월별 사용시간",
        data: useMonthList.value.map((item) => ({
          x: item.usage_month,
          y: Number((Number(item.total_time_sec || 0) / 3600).toFixed(2)),
          seconds: Number(item.total_time_sec || 0)
        })),
        backgroundColor: (context) =>
          createBarGradient(context, "#a78bfa", "#7c3aed"),
        hoverBackgroundColor: "#6d28d9",
        borderWidth: 0,
        borderRadius: 7,
        borderSkipped: false,
        maxBarThickness: 42,
        categoryPercentage: 0.68,
        barPercentage: 0.82
      }
    ]
  };
});

const usageMonthChartOptions = createUsageChartOptions("월");

/**
 * Toast
 */
const toast = reactive({
  show: false,
  message: ""
});

let toastTimer = null;

function showToast(msg) {
  toast.message = msg;
  toast.show = true;

  if (toastTimer) clearTimeout(toastTimer);

  toastTimer = setTimeout(() => {
    toast.show = false;
    toast.message = "";
  }, 2200);
}
</script>

<style scoped>
.page {
  min-height: 100%;
  box-sizing: border-box;
  padding: 24px 20px 40px;
  background: #f4f7fb;
  color: #1f2937;
  display: grid;
  align-content: start;
  gap: 20px;
}

.head {
  display: flex;
  justify-content: space-between;
  gap: 24px;
  align-items: flex-end;
}

.eyebrow {
  margin-bottom: 6px;
  color: #2563eb;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.12em;
}

.h2 {
  margin: 0;
  color: #111827;
  font-size: 28px;
  font-weight: 800;
  letter-spacing: -0.03em;
}

.sub {
  margin: 7px 0 0;
  font-size: 14px;
  line-height: 1.5;
  color: #6b7280;
}

.device-count {
  display: flex;
  align-items: baseline;
  gap: 5px;
  min-width: 126px;
  padding: 12px 16px;
  border: 1px solid #dbe4f0;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.88);
  color: #64748b;
  font-size: 12px;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.04);
}

.device-count strong {
  margin-left: auto;
  color: #1d4ed8;
  font-size: 24px;
  line-height: 1;
}

.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    "Liberation Mono", "Courier New", monospace;
}

.strong {
  color: #111827;
  font-weight: 700;
}

.panel {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  padding: 18px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.05);
}

.toolbar {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: center;
  padding-bottom: 16px;
  border-bottom: 1px solid #edf1f6;
}

.tool-left {
  display: flex;
  flex: 1;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
}

.tool-right {
  display: flex;
  gap: 10px;
  align-items: center;
}

.input {
  box-sizing: border-box;
  min-width: 240px;
  height: 42px;
  border: 1px solid #dbe2ea;
  border-radius: 11px;
  padding: 0 13px;
  outline: none;
  background: #f8fafc;
  color: #1f2937;
  font-size: 13px;
  transition: border-color 0.18s ease, box-shadow 0.18s ease,
    background 0.18s ease;
}

.input:focus {
  border-color: #3b82f6;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12);
}

.input::placeholder {
  color: #94a3b8;
}

.tool-left .input {
  width: min(100%, 420px);
}

.usage-stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-top: 16px;
}

.stat-card {
  display: grid;
  gap: 5px;
  min-height: 104px;
  box-sizing: border-box;
  padding: 17px 18px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
}

.stat-card.primary {
  border-color: #bfdbfe;
  background: #eff6ff;
}

.stat-label {
  color: #64748b;
  font-size: 12px;
  font-weight: 800;
}

.stat-card strong {
  overflow: hidden;
  color: #0f172a;
  font-size: 21px;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.stat-card.primary strong {
  color: #1d4ed8;
}

.stat-note {
  color: #94a3b8;
  font-size: 12px;
}

.distribution-panel {
  margin-top: 14px;
  padding: 16px;
  border: 1px solid #e5eaf0;
  border-radius: 8px;
  background: #fbfcfe;
}

.distribution-head {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 14px;
}

.distribution-head strong {
  display: block;
  color: #0f172a;
  font-size: 14px;
}

.distribution-head span {
  display: block;
  margin-top: 3px;
  color: #64748b;
  font-size: 12px;
}

.distribution-range {
  flex: 0 0 auto;
  padding: 6px 9px;
  border: 1px solid #dbe4f0;
  border-radius: 999px;
  background: #fff;
  color: #475569;
  font-size: 12px;
  font-weight: 800;
}

.distribution-chart {
  height: 260px;
  min-width: 0;
}

.outlier-list {
  display: grid;
  gap: 10px;
  margin-top: 12px;
  padding: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
}

.outlier-list-head {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: baseline;
}

.outlier-list-head strong {
  color: #334155;
  font-size: 12px;
}

.outlier-list-head span {
  color: #64748b;
  font-size: 12px;
}

.outlier-items {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.outlier-items li {
  display: grid;
  gap: 8px;
  min-width: 0;
  padding: 11px;
  border: 1px solid #fed7aa;
  border-radius: 8px;
  background: #fff7ed;
}

.outlier-items div {
  min-width: 0;
}

.outlier-items strong,
.outlier-items span {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.outlier-items strong {
  color: #7c2d12;
  font-size: 12px;
}

.outlier-items span {
  margin-top: 3px;
  color: #9a3412;
  font-size: 11px;
}

.outlier-items b {
  color: #c2410c;
  font-size: 14px;
  line-height: 1.2;
}

.distribution-empty {
  display: grid;
  min-height: 160px;
  place-items: center;
  border: 1px dashed #dbe2ea;
  border-radius: 8px;
  color: #64748b;
  background: #f8fafc;
  font-size: 13px;
}

.table-wrap {
  margin-top: 16px;
  border: 1px solid #e5eaf0;
  border-radius: 14px;
  overflow-x: auto;
  overflow-y: hidden;
}

.table {
  width: 100%;
  min-width: 900px;
  border-collapse: collapse;
  font-size: 13px;
}

.table thead th {
  background: #f8fafc;
  text-align: left;
  padding: 13px 16px;
  border-bottom: 1px solid #e5eaf0;
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: -0.01em;
  white-space: nowrap;
}

.table tbody td {
  padding: 16px;
  border-bottom: 1px solid #edf1f5;
  vertical-align: middle;
  white-space: nowrap;
}

.table tbody tr:last-child td {
  border-bottom: 0;
}

.table tbody tr:hover {
  background: #f8fbff;
}

.right {
  text-align: right;
}

.col-name {
  width: 24%;
}

.col-region,
.col-status,
.col-location {
  width: 13%;
}

.status-head {
  display: inline-flex;
  align-items: center;
  gap: 7px;
}

.status-filter {
  height: 28px;
  min-width: 78px;
  border: 1px solid #dbe4f0;
  border-radius: 8px;
  padding: 0 8px;
  background: #fff;
  color: #334155;
  font-size: 12px;
  font-weight: 700;
}

.col-usage {
  width: 16%;
}

.col-actions {
  width: 250px;
}

.actions {
  display: inline-flex;
  gap: 8px;
  justify-content: flex-end;
  flex-wrap: nowrap;
  align-items: center;
}

.link {
  min-height: 32px;
  border: 1px solid #dbe4f0;
  border-radius: 9px;
  padding: 7px 10px;
  cursor: pointer;
  background: #fff;
  color: #334155;
  font-weight: 700;
  font-size: 12px;
  transition: border-color 0.18s ease, color 0.18s ease, background 0.18s ease;
}

.link:hover {
  border-color: #93c5fd;
  background: #eff6ff;
  color: #1d4ed8;
}

.link-edit {
  border-color: #bbf7d0;
  color: #15803d;
}

.link-edit:hover {
  border-color: #86efac;
  background: #f0fdf4;
  color: #166534;
}

.location {
  display: inline-flex;
  padding: 5px 9px;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  background: #f8fafc;
  color: #475569;
  font-size: 12px;
}

.status-badge {
  display: inline-flex;
  padding: 5px 9px;
  border: 1px solid #bfdbfe;
  border-radius: 999px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 12px;
  font-weight: 800;
}

.status-badge.inactive {
  border-color: #e2e8f0;
  background: #f8fafc;
  color: #64748b;
}

.usage-time {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: #1e3a8a;
}

.usage-icon {
  display: inline-grid;
  width: 28px;
  height: 28px;
  place-items: center;
  border-radius: 8px;
  background: #eff6ff;
  font-size: 13px;
}

.empty {
  text-align: center;
  color: #64748b;
  padding: 54px 16px !important;
}

/* buttons */
.btn {
  min-height: 40px;
  border-radius: 10px;
  padding: 9px 13px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  border: 1px solid transparent;
  white-space: nowrap;
}

.btn-primary {
  background: #1d4ed8;
  color: #fff;
}

.btn-ghost {
  background: #fff;
  border-color: #dbe2ea;
  color: #475569;
}

.btn-ghost:hover {
  border-color: #cbd5e1;
  background: #f8fafc;
}

.btn-dark {
  background: #111827;
  color: #fff;
}

.btn-dark:hover:not(:disabled) {
  background: #1f2937;
}

.btn:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

/* modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.58);
  backdrop-filter: blur(5px);
  display: grid;
  place-items: center;
  padding: 20px;
  z-index: 50;
}

.modal {
  width: 460px;
  max-width: 100%;
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  background: #fff;
  border-radius: 22px;
  padding: 0;
  border: 1px solid rgba(226, 232, 240, 0.9);
  box-shadow: 0 28px 80px rgba(15, 23, 42, 0.3);
}

.modal.wide {
  width: 920px;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 22px 24px;
  border-bottom: 1px solid #edf1f5;
}

.modal-heading {
  display: flex;
  align-items: center;
  gap: 13px;
  min-width: 0;
}

.modal-icon {
  display: grid;
  flex: 0 0 42px;
  width: 42px;
  height: 42px;
  place-items: center;
  border-radius: 13px;
  background: linear-gradient(145deg, #dbeafe, #eff6ff);
  color: #2563eb;
  font-size: 18px;
}

.modal-icon.month {
  background: linear-gradient(145deg, #ede9fe, #f5f3ff);
  color: #7c3aed;
}

.modal-icon.edit {
  background: linear-gradient(145deg, #dcfce7, #f0fdf4);
  color: #15803d;
}

.modal-title {
  color: #0f172a;
  font-size: 19px;
  font-weight: 800;
  letter-spacing: -0.02em;
}

.modal-subtitle {
  margin: 4px 0 0;
  overflow: hidden;
  color: #64748b;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.modal-close {
  display: grid;
  flex: 0 0 36px;
  width: 36px;
  height: 36px;
  place-items: center;
  border: 0;
  border-radius: 10px;
  cursor: pointer;
  background: #f1f5f9;
  color: #64748b;
  font-size: 24px;
  font-weight: 300;
  line-height: 1;
  transition: background 0.18s ease, color 0.18s ease;
}

.modal-close:hover {
  background: #e2e8f0;
  color: #0f172a;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  padding: 20px 24px 0;
}

.summary-card {
  position: relative;
  display: grid;
  gap: 4px;
  overflow: hidden;
  padding: 17px 18px;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background: #f8fafc;
}

.summary-card::after {
  position: absolute;
  top: -28px;
  right: -20px;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(148, 163, 184, 0.09);
  content: "";
}

.summary-card.primary {
  border-color: #bfdbfe;
  background: linear-gradient(135deg, #eff6ff 0%, #f8fbff 100%);
}

.summary-card.primary.month {
  border-color: #ddd6fe;
  background: linear-gradient(135deg, #f5f3ff 0%, #fbfaff 100%);
}

.summary-label {
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
}

.summary-card strong {
  position: relative;
  z-index: 1;
  margin-top: 2px;
  color: #0f172a;
  font-size: 22px;
  letter-spacing: -0.03em;
}

.summary-card.primary strong {
  color: #1d4ed8;
}

.summary-card.primary.month strong {
  color: #6d28d9;
}

.summary-note {
  color: #94a3b8;
  font-size: 11px;
}

.query-panel {
  margin: 16px 24px 0;
  padding: 15px 16px;
  border: 1px solid #e5eaf0;
  border-radius: 14px;
  background: #f8fafc;
}

.query-heading {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 12px;
}

.query-heading strong {
  color: #334155;
  font-size: 13px;
}

.query-heading span {
  color: #94a3b8;
  font-size: 11px;
}

.date-row {
  display: flex;
  gap: 12px;
  align-items: end;
  flex-wrap: wrap;
}

.field {
  display: grid;
  gap: 6px;
}

.date-row .input {
  height: 40px;
  min-width: 190px;
  border-color: #dbe2ea;
  background: #fff;
}

.button-field {
  padding-bottom: 0;
}

.label {
  color: #64748b;
  font-size: 11px;
  font-weight: 700;
}

.query-button {
  min-width: 92px;
  box-shadow: 0 5px 12px rgba(37, 99, 235, 0.2);
}

.chart-section {
  margin: 20px 24px 0;
}

.chart-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 12px;
}

.chart-header > div:first-child {
  display: grid;
  gap: 4px;
}

.chart-header > div:first-child strong {
  color: #1e293b;
  font-size: 14px;
}

.chart-header > div:first-child span {
  color: #94a3b8;
  font-size: 11px;
}

.period-total {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 10px;
  border-radius: 9px;
  background: #eff6ff;
  color: #64748b;
  font-size: 11px;
  white-space: nowrap;
}

.period-total strong {
  color: #1d4ed8;
  font-size: 12px;
}

.period-total.month {
  background: #f5f3ff;
}

.period-total.month strong {
  color: #6d28d9;
}

.chart-wrap {
  box-sizing: border-box;
  height: 340px;
  border: 1px solid #e5eaf0;
  border-radius: 14px;
  padding: 18px 16px 12px;
  background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
}

.chart-empty {
  display: flex;
  min-height: 180px;
  align-items: center;
  justify-content: center;
  gap: 9px;
  border: 1px dashed #dbe2ea;
  border-radius: 14px;
  padding: 32px 12px;
  text-align: center;
  color: #64748b;
  background: #f8fafc;
  font-size: 13px;
}

.spinner {
  width: 17px;
  height: 17px;
  border: 2px solid #bfdbfe;
  border-top-color: #2563eb;
  border-radius: 50%;
  animation: spin 0.75s linear infinite;
}

.spinner.month {
  border-color: #ddd6fe;
  border-top-color: #7c3aed;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.modal-actions {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px 20px;
  border-top: 1px solid #edf1f5;
}

.edit-device-modal {
  width: 560px;
}

.edit-device-form {
  display: grid;
  gap: 17px;
  padding: 20px 24px 0;
}

.edit-field {
  display: grid;
  gap: 7px;
}

.edit-field > span,
.edit-time-field legend {
  color: #334155;
  font-size: 12px;
  font-weight: 800;
}

.edit-field .input {
  width: 100%;
}

.edit-field .input[readonly] {
  border-color: #e2e8f0;
  background: #f1f5f9;
  color: #64748b;
  cursor: not-allowed;
}

.edit-field small,
.edit-time-field small {
  color: #94a3b8;
  font-size: 11px;
}

.edit-time-field {
  display: grid;
  gap: 8px;
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
}

.edit-time-field legend {
  margin-bottom: 7px;
  padding: 0;
}

.edit-time-inputs {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 12px;
}

.edit-time-inputs label {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #475569;
  font-size: 12px;
  font-weight: 700;
}

.edit-time-inputs .input {
  width: 100%;
  min-width: 0;
}

.edit-modal-actions {
  margin: 4px -24px 0;
}

/* 기기별 사용시간 PDF */
.pdf-modal-overlay {
  z-index: 70;
}

.pdf-modal-card {
  display: flex;
  width: min(1280px, 100%);
  height: min(92vh, 920px);
  max-height: calc(100vh - 32px);
  flex-direction: column;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  border-radius: 22px;
  background: #fff;
  box-shadow: 0 28px 80px rgba(15, 23, 42, 0.3);
}

.pdf-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  padding: 18px 22px 16px;
  border-bottom: 1px solid #e5e7eb;
}

.pdf-modal-subtitle {
  margin: 5px 0 0;
  color: #64748b;
  font-size: 12px;
}

.pdf-range-panel {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  padding: 14px 22px;
  border-bottom: 1px solid #e5e7eb;
  background: #f8fafc;
}

.pdf-range-panel .input {
  width: 180px;
  min-width: 0;
  height: 40px;
  background: #fff;
}

.pdf-range-panel .pdf-filter-field .input {
  width: 300px;
}

.range-separator {
  align-self: center;
  margin-top: 18px;
  color: #64748b;
  font-weight: 800;
}

.pdf-result-summary {
  align-self: center;
  margin-left: auto;
  color: #475569;
  font-size: 12px;
  font-weight: 700;
}

.pdf-modal-body {
  position: relative;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  background: #e9eef5;
}

.pdf-frame {
  width: 100%;
  height: 100%;
  border: 0;
  background: #fff;
}

.pdf-preview-empty {
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 24px;
  box-sizing: border-box;
  color: #64748b;
  text-align: center;
  font-size: 13px;
}

.pdf-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 15px 22px 18px;
  border-top: 1px solid #e5e7eb;
}

.pdf-capture-area {
  position: fixed;
  top: 0;
  left: -100000px;
  z-index: -1;
  width: 1587px;
  background: #fff;
}

.pdf-capture-stack {
  width: 1587px;
  background: #fff;
}

.pdf-page {
  display: flex;
  width: 1587px;
  height: 1123px;
  flex-direction: column;
  padding: 54px 58px 38px;
  box-sizing: border-box;
  overflow: hidden;
  background: #fff;
  color: #111827;
}

.pdf-page-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 30px;
  margin-bottom: 24px;
  padding-bottom: 18px;
  border-bottom: 4px solid #1e3a8a;
}

.pdf-page-header h1 {
  margin: 0;
  color: #0f172a;
  font-size: 38px;
  line-height: 1.1;
  letter-spacing: -0.04em;
}

.pdf-page-header p {
  margin: 9px 0 0;
  color: #475569;
  font-size: 19px;
  font-weight: 600;
}

.pdf-page-header > strong {
  color: #475569;
  font-size: 18px;
}

.pdf-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.pdf-table th,
.pdf-table td {
  height: 42px;
  padding: 7px 12px;
  border: 1px solid #94a3b8;
  box-sizing: border-box;
  color: #1e293b;
  font-size: 17px;
  line-height: 1.25;
  text-align: center;
  overflow-wrap: anywhere;
}

.pdf-table th {
  height: 48px;
  background: #e8eef8;
  color: #0f172a;
  font-weight: 800;
}

.pdf-table th:nth-child(2) {
  width: 22%;
}

.pdf-table th:nth-child(3),
.pdf-table th:nth-child(4) {
  width: 12%;
}

.pdf-table th:nth-child(5) {
  width: 16%;
}

.pdf-col-number {
  width: 5%;
}

.pdf-col-usage {
  width: 16%;
}

.pdf-col-period-usage {
  width: 17%;
}

.pdf-table tbody tr:nth-child(even) td {
  background: #f8fafc;
}

.pdf-usage-cell {
  color: #1d4ed8 !important;
  font-weight: 800;
}

.pdf-page-footer {
  display: flex;
  justify-content: space-between;
  margin-top: auto;
  padding-top: 16px;
  color: #64748b;
  font-size: 15px;
}

/* toast */
.toast {
  position: fixed;
  right: 18px;
  bottom: 18px;
  background: rgba(17, 17, 17, 0.92);
  color: #fff;
  padding: 12px 14px;
  border-radius: 12px;
  font-size: 13px;
  z-index: 60;
}

@media (max-width: 980px) {
  .page {
    padding: 20px 6px 32px;
  }

  .head {
    align-items: flex-start;
  }

  .h2 {
    font-size: 24px;
  }

  .panel {
    padding: 14px;
  }

  .toolbar {
    align-items: stretch;
  }

  .tool-left {
    min-width: 0;
  }

  .tool-left .input {
    width: 100%;
  }

  .usage-stats {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .distribution-head {
    flex-direction: column;
  }

  .outlier-items {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .modal {
    width: calc(100vw - 20px);
    max-height: calc(100vh - 20px);
    border-radius: 12px;
  }

  .modal.wide {
    width: 96vw;
  }

  .modal-header {
    padding: 18px;
  }

  .summary-grid {
    padding: 16px 18px 0;
  }

  .query-panel {
    margin: 14px 18px 0;
  }

  .chart-section {
    margin: 18px 18px 0;
  }

  .modal-actions {
    padding: 14px 18px 18px;
  }

  .input {
    min-width: 180px;
  }

  .chart-wrap {
    height: 220px;
    padding: 10px;
  }

  .date-row {
    flex-direction: column;
    align-items: stretch;
  }

  .pdf-modal-card {
    width: 96vw;
    height: calc(100vh - 20px);
    max-height: none;
    border-radius: 14px;
  }

  .pdf-range-panel {
    flex-wrap: wrap;
    padding: 12px 18px;
  }

  .pdf-result-summary {
    width: 100%;
    margin-left: 0;
  }

  .field {
    width: 100%;
  }

  .button-field {
    width: 100%;
  }

  .button-field .btn {
    width: 100%;
  }
}

@media (max-width: 640px) {
  .head {
    flex-direction: column;
  }

  .device-count {
    width: 100%;
    box-sizing: border-box;
  }

  .toolbar {
    flex-direction: column;
  }

  .tool-right,
  .tool-right .btn {
    width: 100%;
  }

  .tool-right {
    flex-wrap: wrap;
  }

  .usage-stats {
    grid-template-columns: 1fr;
  }

  .stat-card {
    min-height: 0;
  }

  .distribution-panel {
    padding: 12px;
  }

  .distribution-chart {
    height: 220px;
  }

  .outlier-list-head {
    align-items: flex-start;
    flex-direction: column;
    gap: 4px;
  }

  .outlier-items {
    grid-template-columns: 1fr;
  }

  .input {
    min-width: 0;
  }

  .modal-overlay {
    padding: 8px;
  }

  .modal.wide {
    width: 100%;
  }

  .modal-header {
    padding: 16px;
  }

  .modal-icon {
    flex-basis: 38px;
    width: 38px;
    height: 38px;
  }

  .modal-title {
    font-size: 17px;
  }

  .summary-grid {
    grid-template-columns: 1fr;
    padding: 14px 14px 0;
  }

  .summary-card {
    padding: 14px 15px;
  }

  .query-panel {
    margin: 14px 14px 0;
  }

  .query-heading {
    align-items: flex-start;
    flex-direction: column;
    gap: 3px;
  }

  .chart-section {
    margin: 16px 14px 0;
  }

  .chart-header {
    align-items: flex-start;
    flex-direction: column;
    gap: 10px;
  }

  .period-total {
    align-self: stretch;
    justify-content: space-between;
  }

  .modal-actions {
    padding: 14px;
  }

  .modal-actions .btn {
    width: 100%;
  }

  .pdf-modal-overlay {
    padding: 0;
  }

  .pdf-modal-card {
    width: 100%;
    height: 100vh;
    border: 0;
    border-radius: 0;
  }

  .pdf-modal-header,
  .pdf-modal-footer {
    padding-right: 14px;
    padding-left: 14px;
  }

  .pdf-range-panel {
    align-items: stretch;
    padding: 12px 14px;
  }

  .pdf-range-panel .field {
    width: calc(50% - 14px);
  }

  .pdf-range-panel .input {
    width: 100%;
  }

  .pdf-range-panel .pdf-filter-field {
    width: 100%;
  }

  .pdf-range-panel .pdf-filter-field .input {
    width: 100%;
  }

  .pdf-range-panel .btn {
    width: 100%;
  }

  .range-separator {
    align-self: flex-end;
    margin-bottom: 11px;
  }
}
</style>
